import NoticesContext from "@jnctn-app/contexts/NoticesContext";
import TenantContext, { Tenant } from "@jnctn/bridge/contexts/TenantContext";
import LocationContext, {
  DEFAULT,
} from "@jnctn/bridge/contexts/LocationContext";
import { Location } from "@jnctn/bridge/contexts/LocationContextShared";
import {
  IncompleteProfileError,
  TenantErrorInactive,
  TenantErrorNoActiveTenants,
  TenantErrorNoTenants,
} from "@jnctn/bridge/auth/authHooks";
import { useUser } from "./core/hooks/authHooks";
import StandardPageLayout from "@jnctn-app/core/components/layout/StandardPageLayout.component";
import { isChecksumValid } from "@jnctn/utilities/isChecksumValid";
import {
  INSTALLABLE_PROMPT_COUNT,
  TENANT,
  LOCATION,
} from "@jnctn/webcore/storage/localStorageKeys";
import useGatekeeper, {
  Gatekeeper,
  useGatekeeperRedirect,
} from "@jnctn-app/core/hooks/useGatekeeper";
import useLocalStorage from "@jnctn/bridge/hooks/useLocalStorage";
import Notice from "@jnctn-app/core/notices/Notice";
import HomePage from "@jnctn-app/pages/HomePage/HomePage";
import React, {
  ComponentProps,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactElement,
} from "react";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import registerServiceWorker from "./registerServiceWorker";
import ErrorUnauthorized from "./core/components/ErrorUnauthorized.component";
import emptyFunction from "@jnctn/core/utilities/emptyFunction";
import ErrorBoundary, {
  ErrorConstructor,
  Fallback,
} from "@jnctn/error-handling/ErrorBoundary.component";
import {
  FetchNotFoundRequest,
  FetchUnauthorizedRequest,
} from "@jnctn/error-handling/Errors";
import useSignalR, { useSignalRHandler } from "@jnctn/api/app/useSignalR";
import { IssuedCredential } from "@jnctn/credentials/api/responses";
import BackContext, { Back } from "./contexts/BackContext";
import {
  TenantErrorPageInactive,
  TenantErrorPageNoActiveTenants,
  TenantErrorPageNoTenants,
} from "./pages/TenantErrorPage/TenantErrorPage";
import { IncompleteProfileErrorPage } from "./pages/IncompleteProfilePage/IncompleteProfile";
import AssignCredentialContextProvider from "./contexts/AssignCredentialContextProvider";
import ThemeProvider from "@jnctn/design-system/ThemeProvider/ThemeProvider.provider";
import AuthContextProvider, {
  useAuthContext,
} from "./contexts/AuthContextProvider";
import Loader from "@jnctn/design-system/Loader/Loader.component";

// Split code by pages using lazy loading + suspense
const ProfilePage = lazy(
  () => import("@jnctn-app/pages/ProfilePage/ProfilePage")
);

const VCVerifierScanPage = lazy(
  () => import("@jnctn-app/pages/VCVerifierPage/VCVerifierPage")
);
const CardPage = lazy(() => import("@jnctn-app/pages/CardPage/CardPage"));
const CredentialsPage = lazy(
  () => import("@jnctn-app/pages/CredentialsPage/CredentialsPage")
);
const SearchPage = lazy(() => import("@jnctn-app/pages/SearchPage/SearchPage"));
const NotFoundPage = lazy(
  () => import("@jnctn-app/pages/NotFoundPage/NotFoundPage")
);
const QRScanPage = lazy(() => import("@jnctn-app/pages/QRScanPage/QRScanPage"));
const CodePage = lazy(() => import("@jnctn-app/pages/CodePage/CodePage"));
const AuthenticationPage = lazy(
  () => import("@jnctn-app/pages/AuthenticationPage/AuthenticationPage")
);
const AssignCredentialPage = lazy(
  () => import("@jnctn-app/pages/AssignCredentialPage/AssignCredentialPage")
);
const NotificationsPage = lazy(
  () => import("@jnctn-app/pages/NotificationsPage/NotificationsPage")
);
const GlobalErrorPage = lazy(
  () => import("@jnctn-app/pages/GlobalErrorPage/GlobalErrorPage")
);
const VerifierCheckPage = lazy(
  () => import("@jnctn-app/pages/VerifierCheckPage/VerifierCheckPage")
);
const VerifierCheckScanPage = lazy(
  () => import("@jnctn-app/pages/VerifierCheckPage/VerifierCheckScanPage")
);
const MorePage = lazy(() => import("@jnctn-app/pages/MorePage/MorePage"));
const SelfCredentialCheckPage = lazy(
  () =>
    import("@jnctn-app/pages/SelfCredentialCheckPage/SelfCredentialCheckPage")
);
const SelectLocationPage = lazy(
  () => import("@jnctn-app/pages/SelectLocationPage/SelectLocationPage")
);
const CovidPassPage = lazy(
  () => import("@jnctn-app/pages/CovidPassPage/CovidPassPage")
);
const CovidPassScanPage = lazy(
  () => import("@jnctn-app/pages/CovidPassPage/CovidPassScanPage")
);

// Checks that a checksum is valid before passing on to Page
// otherwise serves not found page
function ValidChecksumHandler({
  component: Component,
}: {
  component: React.FunctionComponent<{}>;
}) {
  const { checksum } = useParams();
  if (isChecksumValid(checksum!)) {
    return <Component />;
  }
  return <NotFoundPage />;
}

// If this component is rendered then we navigate to login
function AuthRedirect() {
  return <ErrorUnauthorized reset={emptyFunction} />;
}

const TENANT_FALLBACK = new Map<ErrorConstructor, Fallback>([
  [Error, () => <GlobalErrorPage>An error has occurred.</GlobalErrorPage>],
]);

export const GLOBAL_FALLBACK = new Map<ErrorConstructor, Fallback>([
  [FetchUnauthorizedRequest, ErrorUnauthorized],
  [
    TenantErrorNoActiveTenants,
    () => (
      <StandardPageLayout type="gray" hasMenu={false}>
        <TenantErrorPageNoActiveTenants />
      </StandardPageLayout>
    ),
  ],
  [
    IncompleteProfileError,
    ({ error }) => {
      const navigate = useNavigate();
      useEffect(() => {
        navigate("/error/incomplete-profile");
      }, [navigate]);
      return (
        <StandardPageLayout type="gray" hasMenu={false}>
          <IncompleteProfileErrorPage
            defaultTenant={(error as IncompleteProfileError).tenantName}
          />
        </StandardPageLayout>
      );
    },
  ],
  [
    TenantErrorInactive,
    ({ error }) => {
      const navigate = useNavigate();
      useEffect(() => {
        navigate("/error/inactive-tenant");
      }, [navigate]);
      return (
        <StandardPageLayout type="gray" hasMenu={false}>
          <TenantErrorPageInactive
            checksum={(error as TenantErrorInactive).checksum}
          />
        </StandardPageLayout>
      );
    },
  ],
  [
    TenantErrorNoTenants,
    () => (
      <StandardPageLayout type="gray" hasMenu={false}>
        <TenantErrorPageNoTenants />
      </StandardPageLayout>
    ),
  ],
  [
    FetchNotFoundRequest,
    () => (
      <StandardPageLayout type="gray" hasMenu={false}>
        <NotFoundPage />
      </StandardPageLayout>
    ),
  ],
  [Error, () => <GlobalErrorPage>An error has occurred.</GlobalErrorPage>],
]);

function AppOutlet({
  hasAuth = false,
  hasGk,
}: {
  hasAuth?: boolean;
  hasGk?: keyof Gatekeeper;
}) {
  useGatekeeperRedirect(useMemo(() => (hasGk ? [hasGk] : []), [hasGk]));
  const user = useUser();
  const { activeNavigator } = useAuthContext();
  if (
    activeNavigator === "signinRedirect" ||
    activeNavigator === "signoutRedirect"
  ) {
    return (
      <StandardPageLayout type="white">
        <div className="h-screen flex items-center justify-center">
          <Loader />
        </div>
      </StandardPageLayout>
    );
  }

  return !hasAuth || user ? <Outlet /> : <AuthRedirect />;
}

type TransientNotices = Map<string, [number, ComponentProps<typeof Notice>]>;

function useTransientNotices(): [
  TransientNotices,
  (key: string, notice: ComponentProps<typeof Notice>, delay?: number) => void,
  (key: string) => void
] {
  const [transientNotices, setTransientNotices] = useState<TransientNotices>(
    new Map()
  );

  // Allows addition of arbitrary transient notices
  const addTransientNotice = useCallback(
    (
      key: string,
      notice: ComponentProps<typeof Notice>,
      delay: number = 5000
    ) =>
      setTransientNotices((transientNotices) => {
        transientNotices.set(key, [delay, notice]);
        return new Map(transientNotices);
      }),
    [setTransientNotices]
  );

  // Allows removal of arbitrary transient notices
  const removeTransientNotice = useCallback(
    (key: string) =>
      setTransientNotices((transientNotices) => {
        transientNotices.delete(key);
        return new Map(transientNotices);
      }),
    [setTransientNotices]
  );

  return [transientNotices, addTransientNotice, removeTransientNotice];
}

// App entry point, handles PWA functionality, routing and code spliting
// try to keep this page free of non-critical dependencies
export default function App(): ReactElement {
  return (
    <BrowserRouter>
      <AuthContextProvider>
        <AppContent />
      </AuthContextProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  // PWA/Service worker notice state
  const [back, setBack] = useState<Back>({});
  const [update, setUpdate] = useState(false);
  const [install, setInstall] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const [transientNotices, addTransientNotice, removeTransientNotice] =
    useTransientNotices();
  const gks = useGatekeeper();

  // Get the user so the storage keys are personalized
  const user = useUser();

  // Get a signalr connection
  const signalR = useSignalR(
    `${import.meta.env.REACT_APP_QUERY_API_URL}/hub/subscribe`,
    gks.signalr ? user?.access_token : undefined
  );

  // Add a signalr handler
  useSignalRHandler(
    signalR,
    "OnNotification",
    useCallback(
      ({ message }: { message: string; level: number }) =>
        addTransientNotice("signalr-notification", {
          type: "attention",
          text: message,
          actions: [],
        }),
      [addTransientNotice]
    )
  );

  useSignalRHandler(
    signalR,
    "OnCredential",
    useCallback(
      ({
        action,
        credential,
      }: {
        action: number;
        credential: IssuedCredential;
      }) => {
        switch (action) {
          case 1:
            addTransientNotice(`signalr-${credential.id}`, {
              type: "attention",
              text: `Credential Added`,
              actions: [],
            });
            break;
          case 2:
            addTransientNotice(`signalr-${credential.id}`, {
              type: "attention",
              text: `Credential Expiring`,
              actions: [],
            });
            break;
          case 3:
            addTransientNotice(`signalr-${credential.id}`, {
              type: "attention",
              text: `Credential Expired`,
              actions: [],
            });
            break;
        }
      },
      [addTransientNotice]
    )
  );

  type CheckInOutAction = {
    action: number;
    locationId: number;
    locationName: string;
  };

  useSignalRHandler(
    signalR,
    "OnCheckInOut",
    useCallback(
      (action: CheckInOutAction) => {
        switch (action.action) {
          case 1:
            addTransientNotice(`signalr-${action.locationId}-checkin`, {
              type: "attention",
              text: `You have checked in to ${action.locationName}`,
              actions: [],
            });
            break;
          case 2:
            addTransientNotice(`signalr-${action.locationId}-checkout`, {
              type: "attention",
              text: `You have checked out of ${action.locationName}`,
              actions: [],
            });
            break;
        }
      },
      [addTransientNotice]
    )
  );

  // Begin as empty
  const [tenant, setTenant] = useLocalStorage<Tenant | undefined>(
    user ? `${TENANT}-${user.profile.sub}` : TENANT,
    undefined
  );

  // Location from local storage
  const [location, setLocation] = useLocalStorage<Location>(
    user ? `${LOCATION}-${user.profile.sub}` : LOCATION,
    DEFAULT
  );

  // Stores the service worker registration
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();

  // Used to capture the before install prompt event
  const beforeInstallPromptEventRef = useRef<BeforeInstallPromptEvent>(null);

  // For memoization of NoticesContext
  const noticesContext = useMemo(
    () => ({
      // Service worker registration
      registration,

      // SignalR connection
      signalR,

      // Notice state
      update,
      install,
      installable,
      showDevTools,
      transientNotices,

      // Setters
      setInstall,
      setUpdate,
      setInstallable,
      setShowDevTools,

      // Transient notices system
      addTransientNotice,
      removeTransientNotice,

      // Display prompt for install
      addToHomeScreen: () => {
        setInstallable(false);
        if (beforeInstallPromptEventRef.current) {
          beforeInstallPromptEventRef.current.prompt();
          beforeInstallPromptEventRef.current.userChoice.then(() => {
            beforeInstallPromptEventRef.current = undefined;
          });
        }
      },
    }),
    [
      update,
      install,
      installable,
      showDevTools,
      registration,
      transientNotices,
      removeTransientNotice,
      addTransientNotice,
      signalR,
    ]
  );

  // Set up memoized tenant context
  const tenantContext = useMemo(
    () => ({ tenant, setTenant }),
    [tenant, setTenant]
  );

  // Location
  const locationContext = useMemo(
    () => ({ location, setLocation }),
    [location, setLocation]
  );

  const backContext = useMemo(() => ({ back, setBack }), [back, setBack]);

  // Register the service worker and subscribe to update and install success
  // Only register once when the app loads
  useEffect(() => {
    // Don't register the service worker in react snap
    if (navigator.userAgent === "ReactSnap") {
      return;
    }
    registerServiceWorker({
      onUpdate: () => setUpdate(true),
      onInstall: () => setInstall(true),
      onRegister: (reg: ServiceWorkerRegistration) => setRegistration(reg),
    });
  }, []);
  // Use a count for the times the installable prompt has shown, we don't want
  // to annoy users
  const [installablePromptCount, setInstallablePromptCount] = useLocalStorage(
    INSTALLABLE_PROMPT_COUNT,
    0
  );

  // Capture the beforeinstallprompt event
  useEffect(() => {
    // Capture the prompt event
    function isInstallable(event: BeforeInstallPromptEvent) {
      if (installablePromptCount < 3) {
        event.preventDefault();
        beforeInstallPromptEventRef.current = event;
        setInstallable(true);
        setInstallablePromptCount(installablePromptCount + 1);
      }
    }
    window.addEventListener("beforeinstallprompt", isInstallable);
    return () => {
      window.removeEventListener("beforeinstallprompt", isInstallable);
    };
  }, [installablePromptCount, setInstallablePromptCount]);

  // The app is mostly dark
  useDisabledFirefoxNightMode();

  const shouldLog = useCallback(
    (error: Error) =>
      error instanceof FetchUnauthorizedRequest ? user !== undefined : true,
    [user]
  );

  const routeLocation = useLocation();

  return (
    <ErrorBoundary fallback={TENANT_FALLBACK}>
      <ThemeProvider inputVariant="next" buttonVariant="next" theme="default">
        <TenantContext.Provider value={tenantContext}>
          <ErrorBoundary
            key={routeLocation.pathname}
            fallback={GLOBAL_FALLBACK}
            shouldLog={shouldLog}
          >
            <BackContext.Provider value={backContext}>
              <LocationContext.Provider value={locationContext}>
                <NoticesContext.Provider value={noticesContext}>
                  <div className="bg-pink-300 font-extrabold h-48">
                    {process.env.REACT_APP_QUERY_API_URL}
                  </div>
                  <AppRoutes />
                </NoticesContext.Provider>
              </LocationContext.Provider>
            </BackContext.Provider>
          </ErrorBoundary>
        </TenantContext.Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Disables night mode if it is enabled on Firefox
// this is a crappy hack
function useDisabledFirefoxNightMode() {
  useEffect(() => {
    const sheets = Array.from(document.querySelectorAll("style"));
    sheets.forEach((sheet) => {
      if (
        sheet.textContent?.includes(
          "-webkit-filter: hue-rotate(180deg) invert(100%) !important;"
        )
      ) {
        sheet.parentNode?.removeChild(sheet);
      }
    });
  }, []);
}

function useTrackPageView() {
  const { pathname: path } = useLocation();
  // Sets up page tracking for router
  useEffect(() => {
    window.rg4js("trackEvent", { type: "pageView", path });
  }, [path]);
}

function useAutomaticServiceWorkerUpdate() {
  const { registration } = useContext(NoticesContext);
  // Check for service worker updates on path changes
  const { pathname } = useLocation();
  useEffect(() => {
    // Only attempt to update when online
    if (!registration || !window.navigator.onLine) {
      return;
    }
    registration.update().catch(() => {});
  }, [pathname, registration]);
}

function usePersonalizedRaygun() {
  const user = useUser();
  // Register the user session with raygun
  useEffect(() => {
    if (user && user.profile.preferred_username) {
      window.rg4js("setUser", {
        identifier: user.profile.preferred_username,
        isAnonymous: false,
      });
    } else {
      window.rg4js("getRaygunInstance")?.resetAnonymousUser();
    }
  }, [user]);
}

function AppRoutes() {
  const { isAuthenticated } = useAuthContext();
  useTrackPageView();
  useAutomaticServiceWorkerUpdate();
  usePersonalizedRaygun();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/*" element={<AppOutlet />}>
          <Route
            path="*"
            element={
              <StandardPageLayout type="gradient" hasMenu={false}>
                <HomePage />
              </StandardPageLayout>
            }
          />
        </Route>
        <Route path="/scan/" element={<AppOutlet />}>
          <Route
            index
            element={
              <StandardPageLayout
                hasMenu={false}
                hasMenuShadow={false}
                menuColor={"white"}
                type="black"
              >
                <QRScanPage />
              </StandardPageLayout>
            }
          />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<AppOutlet />}>
        <Route
          index
          element={
            <StandardPageLayout type="white" hasMenu>
              <HomePage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path="/code/" element={<AppOutlet />}>
        <Route
          index
          element={
            <StandardPageLayout type="white" hasMenu={false}>
              <CodePage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path="/search/" element={<AppOutlet />}>
        <Route
          index
          element={
            <StandardPageLayout type="white" hasMenu={false}>
              <SearchPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path="/scan/" element={<AppOutlet />}>
        <Route
          index
          element={
            <StandardPageLayout
              hasMenu
              hasMenuShadow={false}
              menuColor={"black"}
              type="black"
            >
              <QRScanPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="/verify/*"
        element={<AppOutlet hasAuth={true} hasGk="verify" />}
      >
        <Route
          index
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <VerifierCheckScanPage />
            </StandardPageLayout>
          }
        />
        <Route
          path="vc"
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <VCVerifierScanPage />
            </StandardPageLayout>
          }
        />
        <Route
          path=":checksum"
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <VerifierCheckPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="/location/"
        element={<AppOutlet hasAuth={true} hasGk="verify" />}
      >
        <Route
          index
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <SelectLocationPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="/selfcheck/*"
        element={<AppOutlet hasAuth={true} hasGk="selfCheck" />}
      >
        <Route
          path=":location"
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <SelfCredentialCheckPage />
            </StandardPageLayout>
          }
        />
        <Route
          index
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <SelfCredentialCheckPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="/pass/*"
        element={<AppOutlet hasAuth={true} hasGk="covidPass" />}
      >
        <Route
          path=":version/:token"
          element={
            <StandardPageLayout type="gray" hasMenu={false}>
              <CovidPassPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path="/profile/" element={<AppOutlet hasAuth={true} />}>
        <Route
          index
          element={
            <StandardPageLayout type="white" hasMenu={false}>
              <ProfilePage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="/assign/*"
        element={<AppOutlet hasAuth={true} hasGk="auth" />}
      >
        <Route
          path=":batchid/*"
          element={
            <AssignCredentialContextProvider>
              <StandardPageLayout type="white" hasMenu={false}>
                <AssignCredentialPage />
              </StandardPageLayout>
            </AssignCredentialContextProvider>
          }
        />
        <Route
          path="*"
          element={
            <StandardPageLayout type="white" hasMenu={false}>
              <AssignCredentialPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path=":checksum/credentials/*" element={<AppOutlet />}>
        <Route
          index
          element={
            <StandardPageLayout type="gray">
              <ValidChecksumHandler component={CredentialsPage} />
            </StandardPageLayout>
          }
        />
        <Route
          path="credential/:credentialId"
          element={
            <StandardPageLayout type="gray">
              <ValidChecksumHandler component={CredentialsPage} />
            </StandardPageLayout>
          }
        />
        <Route
          path=":credentialType"
          element={
            <StandardPageLayout type="gray">
              <ValidChecksumHandler component={CredentialsPage} />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path=":checksum/"
        element={
          <StandardPageLayout
            type="gradient"
            hasMenu={false}
            hasMenuShadow={false}
          >
            <ValidChecksumHandler component={CardPage} />
          </StandardPageLayout>
        }
      />
      <Route
        path="/authentication/*"
        element={
          <StandardPageLayout hasMenu={false}>
            <AuthenticationPage />
          </StandardPageLayout>
        }
      />
      <Route path="/notifications/" element={<AppOutlet hasAuth={true} />}>
        <Route
          index
          element={
            <StandardPageLayout type="white">
              <NotificationsPage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route path="/more/" element={<AppOutlet hasAuth={true} />}>
        <Route
          index
          element={
            <StandardPageLayout type="white">
              <MorePage />
            </StandardPageLayout>
          }
        />
      </Route>
      <Route
        path="*"
        element={
          <StandardPageLayout type="gradient">
            <NotFoundPage />
          </StandardPageLayout>
        }
      />
    </Routes>
  );
}
